import { useContext, useEffect, useRef, useState } from 'react';
import { Box, Divider } from '@mui/material';

import { AuthStoreContext } from '../Root';
import ModeSwitch, { DashboardMode } from './ModeSwitch';
import NodeLayout from './node/NodeLayout';
import DeviceLayout from './device/DeviceLayout';
import KosaLayout from './kosa/KosaLayout';
import { Selection } from './selection';

const MODE_COOKIE = 'dashboard_mode';

function readModeCookie(): DashboardMode {
    const match = document.cookie.match(new RegExp(`(?:^|; )${MODE_COOKIE}=(\\d+)`));
    if (match) {
        const val = Number(match[1]);
        if (val in DashboardMode) return val as DashboardMode;
    }
    return DashboardMode.Locations;
}

function writeModeCookie(mode: DashboardMode) {
    const maxAge = 365 * 24 * 60 * 60;
    document.cookie = `${MODE_COOKIE}=${mode}; path=/; max-age=${maxAge}; `;
}

//=================================================================================================
interface DashboardSideBarProps {
    selection?: Selection;
    onSelect: (sel: Selection) => void;
}

export default function DashboardSideBar({ selection, onSelect }: DashboardSideBarProps) {
    const { authStore } = useContext(AuthStoreContext);
    const user = authStore.user!;

    const [mode, setMode] = useState<DashboardMode>(() => readModeCookie());
    const [visited, setVisited] = useState<Set<DashboardMode>>(() => new Set([readModeCookie()]));
    const scrollRef = useRef<HTMLDivElement>(null);

    // Переключение режима по флагу switchMode в selection
    useEffect(() => {
        if (!selection?.switchMode) return;
        let newMode: DashboardMode;
        //prettier-ignore
        switch (selection.kind) {
            case 'kosa': newMode = DashboardMode.Sensors; break;
            case 'device': newMode = DashboardMode.Devices; break;
            case 'node':
            case 'location': newMode = DashboardMode.Locations; break;
        }
        setMode(newMode!);
        setVisited((prev) => new Set(prev).add(newMode!));
        writeModeCookie(newMode!);
        // Прокрутка к выбранному элементу после переключения режима
        setTimeout(() => {
            const selectedEl = scrollRef.current?.querySelector('.Mui-selected');
            selectedEl?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }, 0);
    }, [selection]);

    const modeBehavior = (newMode: DashboardMode) => {
        setMode(newMode);
        setVisited((prev) => new Set(prev).add(newMode));
        writeModeCookie(newMode);
    };

    return (
        <>
            <ModeSwitch
                state={mode}
                setState={modeBehavior}
                onAll={() => onSelect({ kind: 'node', nodeId: user.node_id })}
            />
            <Divider />
            <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {visited.has(DashboardMode.Locations) && (
                    <Box sx={{ display: mode === DashboardMode.Locations ? 'block' : 'none' }}>
                        <NodeLayout selection={selection} onSelect={onSelect} />
                    </Box>
                )}
                {visited.has(DashboardMode.Devices) && (
                    <Box sx={{ display: mode === DashboardMode.Devices ? 'block' : 'none' }}>
                        <DeviceLayout selection={selection} onSelect={onSelect} />
                    </Box>
                )}
                {visited.has(DashboardMode.Sensors) && (
                    <Box sx={{ display: mode === DashboardMode.Sensors ? 'block' : 'none' }}>
                        <KosaLayout selection={selection} onSelect={onSelect} />
                    </Box>
                )}
            </Box>
        </>
    );
}
